import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Interface for agent dropdown item
 */
export interface AgentDropdownItem {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive';
}

/**
 * Hook to fetch AI agents for dropdown
 *
 * Only returns ACTIVE agents by default for use in workflow configuration
 *
 * @param options - Query options
 * @returns Query result with list of agents
 *
 * @example
 * ```tsx
 * const { data: agents, isLoading } = useAgentsDropdown();
 *
 * <select>
 *   {agents?.map(agent => (
 *     <option key={agent.id} value={agent.id}>
 *       {agent.name}
 *     </option>
 *   ))}
 * </select>
 * ```
 */
export const useAgentsDropdown = (options?: { enabled?: boolean }) => {
  const isEnabled = options?.enabled !== undefined ? options.enabled : true;

  return useQuery({
    queryKey: ['agents-dropdown'],
    queryFn: async () => {
      // Cross-module dropdown route (no agents.read permission required).
      // Backend already filters to ACTIVE agents and returns a flat array.
      const response = await api.get('/ai-agents/dropdown');

      // Backend returns { success: true, data: [...] }
      const items = response.data.data || [];

      // Map to dropdown format
      return items.map((agent: any): AgentDropdownItem => ({
        id: agent.id,
        name: agent.name,
        description: agent.description,
        status: agent.status
      }));
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - agents change infrequently
    refetchOnWindowFocus: false,
    enabled: isEnabled,
  });
};
